import { DisplayReturn } from "@redotech/customer-portal-sdk/rpc/schema/get-returns-by-order-id";
import { useLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { Order } from "@redotech/redo-model/order";
import {
  CompensationMethod,
  ReturnTypeEnum,
} from "@redotech/redo-model/return";
import {
  ReturnStatus,
  isReturnStatusDisplayedRejected,
} from "@redotech/redo-model/return-status";
import { LineItem } from "@redotech/redo-model/shopify-order";
import { useContext } from "react";

// Define ReturnedLineItem interface
export interface ReturnedLineItem extends LineItem {
  _returnId?: string;
  _returnedItem?: boolean;
}

/**
 * Hook to fetch returns for a specific order
 * @param order The order to fetch returns for
 * @returns Loading state and returns data
 */
export function useOrderReturns(order: Order) {
  const orderId = order._id;
  const rpcClient = useContext(RpcClientContext);

  const returnsLoad = useLoad(async () => {
    if (!rpcClient || !orderId) {
      return { returns: [], returnableItems: [] };
    }

    try {
      // Call the RPC method to get returns by order ID
      const draftReturn = await rpcClient.createDraftReturn({
        returnType: ReturnTypeEnum.RETURN,
        orderId,
      });
      const result = await rpcClient.getReturnsByOrderId({ orderId });
      return {
        ...result,
        returnableItems: draftReturn.draftReturn.returnableItems,
      };
    } catch (error) {
      console.error("Error fetching returns for order", orderId, error);
      // Return empty array for returns to avoid breaking the UI
      return { returns: [], returnableItems: [] };
    }
  }, [rpcClient, orderId]);

  return {
    isLoading: returnsLoad.pending,
    returns: returnsLoad.value?.returns || [],
    returnableItems: returnsLoad.value?.returnableItems || [],
    hasError: !!returnsLoad.error,
    error: returnsLoad.error,

    getReturnsByReturnId: () => {
      const returns = returnsLoad.value?.returns || [];
      return groupReturnsByReturnId(returns);
    },

    getReturnCompletionMessage: (returnId: string) => {
      const returns = returnsLoad.value?.returns || [];
      const returnData = returns.find((r) => r._id === returnId);
      return getReturnCompletionMessage(returnData);
    },

    getReturnHeaderText: (returnId: string) => {
      const returns = returnsLoad.value?.returns || [];
      const returnData = returns.find((r) => r._id === returnId);
      return getReturnHeaderText(returnData);
    },
  };
}

/**
 * Group returns by return ID
 */
function groupReturnsByReturnId(
  returns: DisplayReturn[],
): Map<string, DisplayReturn> {
  if (!returns || returns.length === 0) {
    return new Map();
  }

  // Create a map of returnId -> return object
  const returnMap = new Map<string, DisplayReturn>();
  for (const returnData of returns) {
    returnMap.set(returnData._id, returnData);
  }

  return returnMap;
}

/**
 * Get the appropriate return/exchange completion message
 */
function getReturnCompletionMessage(
  returnData: DisplayReturn | undefined,
): string {
  // Check return status first
  if (!returnData) return "Your return was rejected.";

  const returnStatus = returnData.status;

  const isRejected =
    isReturnStatusDisplayedRejected[returnStatus as ReturnStatus];

  // Check if the return is rejected
  if (isRejected) {
    return "Your return was rejected.";
  }

  // Check if the return is open
  if (returnStatus === ReturnStatus.OPEN) {
    return "Your return is waiting to be processed.";
  }

  // Check return strategy for completed returns
  if (returnStatus === ReturnStatus.COMPLETE) {
    switch (returnData.type) {
      case ReturnTypeEnum.CLAIM: {
        return "Your claim is complete.";
      }
      case ReturnTypeEnum.WARRANTY: {
        return "Your warranty is complete.";
      }
      case ReturnTypeEnum.MANAGED_CLAIM: {
        return "Your managed claim is complete.";
      }
      // No default
    }
    if (returnData.returnType === CompensationMethod.STORE_CREDIT) {
      return "Your return is complete and store credit has been issued.";
    } else if (returnData.returnType === CompensationMethod.EXCHANGE) {
      return "Your exchange is complete.";
    } else {
      return "Your return is complete and a refund has been issued.";
    }
  }

  // Default message for any other case
  return "Your return is being processed.";
}

/**
 * Get the appropriate return/exchange header text
 */
function getReturnHeaderText(returnData: DisplayReturn | undefined): string {
  // Handle no return data
  if (!returnData) {
    return "Return rejected";
  }

  const returnStatus = returnData.status;
  const isRejected =
    (ReturnStatus.REJECTED || ReturnStatus.DELETED) === returnStatus;
  // For completed returns
  if (returnStatus === ReturnStatus.COMPLETE) {
    if (returnData.returnType === "exchange") {
      return "Exchange complete";
    }
    return "Return complete";
  }

  // For open/in progress returns
  if (!isRejected) {
    return "Return in progress";
  }

  return "Return rejected";
}
